import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import { DataTable } from './DataTable';

const getQueryData = (filters: any, filterByOptions: any, queryParams: any, queries: any, filterByToggle: any, isToggleOn: boolean) => {
  let queryData: any = {};
  if (queryParams.length) {
    queryParams.forEach((param: { key: string, value: string }) => {
      queryData[param.key] = param.value;
    });
  }
  queryData = { ...queryData, ...queries };
  if (filterByOptions?.length > 0) {
    if (filters.filterBy.match('dateRange')) {
      const dateRangeData = filterByOptions.find((option: any) => option.key === filters.filterBy);
      queryData = {
        ...queryData,
        [dateRangeData.fields[0]]: filters.filterValue.startDate,
        [dateRangeData.fields[1]]: filters.filterValue.endDate,
      };
    } else if (filters.filterBy === 'date') {
      const date = filterByOptions.find((option: any) => option.key === 'date');
      queryData = {
        ...queryData,
        [date.value]: filters.filterValue,
      };
    } else if (filters.filterBy.match('status')) {
      const status = filterByOptions.find((option: any) => option.key === filters.filterBy);
      queryData = {
        ...queryData,
        [status.filterKeyName || status.key]: status.include
          ? [filters.filterValue]
          : status?.boolean ? filters.filterValue === 'true' : filters.filterValue
      };
    } else {
      queryData = {
        ...queryData,
        [filters.filterBy]: filters.filterValue,
      };
    }
  }

  if (isToggleOn) {
    queryData[filterByToggle.key] = filterByToggle.value;
  }

  return queryData;
};

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export const SuperDataTable: React.FC<any> = ({
  query,
  queryParams = [],
  queryVariablesKey = 'data',
  toggle = {},
  fields,
  filterByOptions = [],
  selector,
  noItemsViewText = 'No items',
  scopedSlots,
  showToggle = false,
  showFilter = true,
  showPagination = true,
  showItemsPerPage = true,
  reflectFiltersInUrl = false,
  hideTableIfEmpty = false,
  itemsPerPage = 10,
}: any) => {
  const history = useHistory();
  const urlQueryParams = useQuery();
  const filterByParam = urlQueryParams.get('filterBy') || filterByOptions[0]?.key;
  const filterValueParam = JSON.parse(urlQueryParams.get('filterValue') || '""');
  const isTggleOnParam = urlQueryParams.get(toggle?.key) === 'true';

  const [filters, _setFilters] = useState({ filterValue: filterValueParam, filterBy: filterByParam }) as any;
  const [filterByToggle] = useState({ value: toggle?.value, key: toggle?.key });
  const [isToggleOn, _setIsToggleOn] = useState(isTggleOnParam);
  const [queries, setQueries] = useState<any>({ page: 0, size: itemsPerPage });
  const queryData = getQueryData(filters, filterByOptions, queryParams, queries, filterByToggle, isToggleOn);

  useEffect(() => {
    _setIsToggleOn(isTggleOnParam);
    _setFilters({ filterValue: filterValueParam, filterBy: filterByParam });
  }, [urlQueryParams.toString()]);

  function setIsToggleOn(value: boolean) {
    _setIsToggleOn(value);
    if (reflectFiltersInUrl) {
      urlQueryParams.set(toggle?.key, value.toString());
      history.push({
        search: urlQueryParams.toString(),
      });
    }
  }

  function setFilters(filter: any) {
    _setFilters(filter);
    if (reflectFiltersInUrl) {
      urlQueryParams.set('filterBy', filter.filterBy);
      urlQueryParams.set('filterValue', JSON.stringify(filter.filterValue));
      history.push({
        search: urlQueryParams.toString(),
      });
    }
  }

  const { loading, data } = query({
    variables: {
      [queryVariablesKey]: queryData,
    },
    notifyOnNetworkStatusChange: true,
  });

  useEffect(() => {
    if (filterByOptions[0]?.withId?.key && filterByOptions[0].values) {
      setFilters({ ...filters, filterValue: Object.keys(filterByOptions[0].values)[0] })
    }
    // eslint-disable-next-line
  }, []);

  return (
    !hideTableIfEmpty || data?.[selector]?.results.length || loading ?
      <DataTable
        noItemsViewText={noItemsViewText}
        data={data?.[selector]?.results || []}
        toggleLabel={toggle?.label}
        showToggle={showToggle}
        isToggleOn={isToggleOn}
        setIsToggleOn={setIsToggleOn}
        showFilter={showFilter}
        showPagination={showPagination}
        showItemsPerPage={showItemsPerPage}
        activePage={queries.page + 1}
        itemsPerPage={queries.size}
        fields={fields}
        filterBy={filters.filterBy}
        filterValue={filters.filterValue}
        filterByOptions={filterByOptions}
        onFilterChange={setFilters}
        totalEntries={data?.[selector]?.pagination?.totalResults}
        onPaginationChange={({ page, size }) => {
          setQueries({ ...queries, page: page - 1, size });
        }}
        onSorterChange={(values) => setQueries({
          ...queries,
          sort: values.filter(({ order }) => !!order).map(({ by, order }) => `${by};${order}`)
        })}
        scopedSlots={scopedSlots}
        loading={loading}
      />
    : <span>{noItemsViewText}</span>
  );
};
